import React, { useEffect, useState, useMemo } from "react";
import s from "../Profile.module.css";
import { useAuth } from "../context/AuthContext";
import { useAppTranslation } from "./TranslationContext";
import { Link } from "react-router-dom";

// Компонент карточки курса
const CourseCard = ({ course, index }) => (
  <div className={s.profile__course__wrap}>
    <Link className={s.course_link} to={"/course/" + course.id}>
      <div className={s.profile__course}>
        <p className={s.profile__course__title}>{course.title}</p>
        <p className={s.profile__course__info}>{course.info}</p>
        <img
          src={course.img}
          alt=""
          className={s.profile__course__img}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/images/default.png";
          }}
        />
      </div>
    </Link>

    {/* Кнопка сертификата — если есть */}
    {course.certLink ? (
      <button
        className={s.certBtn}
        onClick={(e) => {
          e.stopPropagation();
          // если рядом Link, чтобы клик не вел на страницу курса
          window.open(course.certLink, "_blank", "noopener,noreferrer");
        }}
      >
        Сертификат
      </button>
    ) : null}
  </div>
);

export const Profile = () => {
  const [userData, setUserData] = useState([]); // из /user-kurs/
  const [loadingUser, setLoadingUser] = useState(true);
  const [errorUser, setErrorUser] = useState(null);

  const [myCourses, setMyCourses] = useState([]);           // полные данные моих курсов
  const [allCourses, setAllCourses] = useState([]);         // все курсы из /kurslar/
  const [recommendedCourses, setRecommendedCourses] = useState([]);

  const { user, handleLang } = useAuth();
  const { t } = useAppTranslation();

  // Утилита: нормализуем возможный пагинированный ответ DRF
  const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.results)) return payload.results;
    return [];
  };

  // 1) Получаем user-kurs
  useEffect(() => {
    if (!user?.id) return;

    fetch(`https://api.eduskill.uz/api/user-kurs/?user=${user.id}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Ошибка сети: " + response.status);
        }
        return response.json();
      })
      .then((json) => {
        setUserData(Array.isArray(json) ? json : []);
        setLoadingUser(false);
      })
      .catch((err) => {
        setErrorUser(err.message);
        setLoadingUser(false);
      });
  }, [user?.id]);

  // 2) Получаем ВСЕ курсы (для рекомендаций)
  useEffect(() => {
    fetch("https://api.eduskill.uz/api/kurslar/")
      .then((res) => {
        if (!res.ok) throw new Error("Kurslarni olishda xatolik");
        return res.json();
      })
      .then((data) => {
        const list = normalizeList(data);
        setAllCourses(list);
      })
      .catch((err) => {
        console.error("Barcha kurslarni olishda xatolik:", err);
        setAllCourses([]); // безопасно
      });
  }, []);

  // 3) Получаем полные данные моих курсов
  useEffect(() => {
    if (!userData || userData.length === 0) {
      setMyCourses([]);
      return;
    }

    const ids = userData.map((k) => k.kurs).filter(Boolean);
    if (ids.length === 0) {
      setMyCourses([]);
      return;
    }

    const uniqueIds = [...new Set(ids)];
    const idParam = uniqueIds.join(",");

    fetch(`https://api.eduskill.uz/api/kurslar/?id__in=${idParam}`)
      .then((res) => {
        if (!res.ok) throw new Error("Kurslarni olishda xatolik");
        return res.json();
      })
      .then((data) => {
        // Этот эндпоинт, судя по опыту, отдаёт массив
        setMyCourses(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Kurs olish xatosi:", err);
        setMyCourses([]);
      });
  }, [userData]);

  // 4) Формируем рекомендации (исключим уже приобретённые)
  useEffect(() => {
    const myIds = new Set(userData.map((k) => k.kurs));
    const pool = allCourses.filter((c) => !myIds.has(c.id));

    // Перемешиваем и берём до 6 позиций
    const shuffled = [...pool].sort(() => 0.5 - Math.random());
    setRecommendedCourses(shuffled.slice(0, 6));
  }, [allCourses, userData]);

  // 5) Готовим словарь { kursId: { mavzu, sertifikat } }
  const userKursMap = useMemo(() => {
    const map = {};
    userData.forEach((item) => {
      map[item.kurs] = {
        mavzu: item.mavzu ?? 0,
        sertifikat: (item.sertifikat ?? "").trim(),
      };
    });
    return map;
  }, [userData]);

  // Загрузка / Ошибка
  if (loadingUser) return <div>Yuklanmoqda...</div>;
  if (errorUser) return <div>Xatolik: {errorUser}</div>;

  return (
    <section className={s.profile}>
      <div className={s.profile__hero}>
        <div className={s.profile__hero__text}>
          <p className={s.profile__title}>{t("hello")} {user?.username}!</p>
          <p className={s.profile__subtitle}>{t("profileSub")}</p>
          <Link to="/more" className={s.moreBtn}>{t("profileCustom")}</Link>
        </div>
        <div className={s.profile__hero__img}>
          <img src="/images/profile_bg.jpg" alt="img" />
        </div>
      </div>

      {/* Мои курсы */}
      <div className={s.profile__mycourses}>
        <p className={s.profile__courses__title}>{t("myCourses")}</p>

        <div className={s.profile__mycourses__courses}>
          {myCourses.map((course, index) => {
            const userKurs = userKursMap[course.id];
            const certLink =
              userKurs && userKurs.sertifikat ? userKurs.sertifikat : "";

            return (
              <CourseCard
                key={course.id}
                index={index}
                course={{
                  id: course.id,
                  title: handleLang(
                    course.nomi_uz,
                    course.nomi_ru,
                    course.nomi_en
                  ),
                  info: handleLang(
                    course.description_uz,
                    course.description_ru,
                    course.description_en
                  ),
                  img: course.img_uz,
                  certLink, // <-- передаём ссылку сертификата
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Рекомендуемые курсы */}
      <div className={s.profile__courses}>
        <p className={s.profile__courses__title}>{t("popCourses")}</p>

        <div className={s.profile__mycourses__courses}>
          {recommendedCourses.map((course, index) => (
            <CourseCard
              key={course.id}
              index={index}
              course={{
                id: course.id,
                title: handleLang(
                  course.nomi_uz,
                  course.nomi_ru,
                  course.nomi_en
                ),
                info: handleLang(
                  course.description_uz,
                  course.description_ru,
                  course.description_en
                ),
                img: course.img_uz,
                certLink: "", // у рекомендаций сертификата ещё нет
              }}
            />
          ))}

          {/* Если по какой-то причине список пуст, покажем fallback */}
          {recommendedCourses.length === 0 && (
            <div className={s.emptyMsg}>
              {t("noRecommendations") || "Hozircha tavsiya etilgan kurslar yoʻq."}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
